/* Custom Dropdown <kg-select>: baut aus einem nativen <select> einen Button mit Liste, optional mit Suchfeld (data-searchable). Optionen können data-flag, data-image und data-meta tragen. Mit data-sheet öffnet die Liste auf kleinen Bildschirmen als Bottom Sheet. */

const ICON = (name, path) =>
  `<svg class="icon select__${name}" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="${path}"/></svg>`;
const CHEVRON = ICON('chevron', 'm6 9 6 6 6-6');
const CHECK = ICON('check', 'm5 12 5 5 9-10');
const CLOSE = ICON('close-icon', 'M6 6l12 12M18 6 6 18');
const BELL = ICON('bell-icon', 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9m4.3 13a2 2 0 0 0 3.4 0');
const SHEET_QUERY = '(max-width: 1023.98px)';
/* Darunter lohnt sich das Aufklappen nach unten nicht mehr: knapp drei Zeilen plus Polster. */
const MIN_UNTEN = 220;
/* Hoechste Hoehe der Liste. Dieselbe Zahl steht als `max-height` an `.select__popover`. */
const MAX_HOEHE = 340;

class KgSelect extends HTMLElement {
  connectedCallback() {
    this.native = this.querySelector('select');
    if (!this.native || this.button) return;
    this.searchable = this.hasAttribute('data-searchable');
    this.build();
    this.native.classList.add('select__native');
    this.classList.add('select', 'select--ready');
    this.native.addEventListener('change', () => this.sync());
    this.native.form?.addEventListener('reset', () => requestAnimationFrame(() => this.sync()));
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close(false);
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  /*
    Fuehrt die Liste ueberhaupt Bilder? Nur dann braucht ein Eintrag ohne eigenes Bild eine
    Platzhalterflaeche — sonst ruecken Name und Bild in der Liste gegeneinander, sobald bei
    einer Sorte das Variantenbild fehlt.
  */
  get mitBildern() {
    if (this._mitBildern === undefined) {
      this._mitBildern = [...this.native.options].some((o) => o.dataset.image);
    }
    return this._mitBildern;
  }

  build() {
    const id = this.native.id || `kg-select-${Math.random().toString(36).slice(2, 8)}`;
    this.native.id = id;
    const label = this.native.labels?.[0];
    if (label && !label.id) label.id = `${id}-label`;
    this.label = label;

    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'select__button';
    this.button.id = `${id}-button`;
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    if (label) this.button.setAttribute('aria-labelledby', `${label.id} ${this.button.id}`);
    const side = this.dataset.side ? `<span class="select__value-side"></span>` : '';
    this.button.innerHTML = `<img class="select__flag" alt="" hidden><img class="select__image" alt="" hidden><span class="select__value-wrap"><span class="select__value"></span><span class="select__value-meta" hidden></span></span>${side}${CHEVRON}`;
    this.flag = this.button.querySelector('.select__flag');
    this.image = this.button.querySelector('.select__image');
    this.value = this.button.querySelector('.select__value');
    this.valueMeta = this.button.querySelector('.select__value-meta');
    const sideElement = this.button.querySelector('.select__value-side');
    if (sideElement) sideElement.textContent = this.dataset.side;

    this.panel = document.createElement('div');
    this.panel.className = 'select__popover';
    this.panel.hidden = true;

    if (this.searchable) {
      const wrap = document.createElement('div');
      wrap.className = 'select__search-wrap';
      this.search = document.createElement('input');
      this.search.type = 'search';
      this.search.className = 'select__search';
      this.search.placeholder = this.dataset.searchPlaceholder || '';
      this.search.autocomplete = 'off';
      this.search.setAttribute('aria-label', this.dataset.searchPlaceholder || '');
      this.clear = document.createElement('button');
      this.clear.type = 'button';
      this.clear.className = 'select__clear';
      this.clear.setAttribute('aria-label', this.dataset.clearLabel || '');
      this.clear.innerHTML = CLOSE;
      this.clear.hidden = true;
      this.clear.addEventListener('click', () => {
        this.search.value = '';
        this.filter('');
        this.search.focus();
      });
      wrap.append(this.search, this.clear);
      this.panel.append(wrap);
    }

    this.scroller = document.createElement('kg-scroll-fade');
    this.scroller.className = 'select__scroll';
    this.list = document.createElement('ul');
    this.list.className = 'select__list scrollbar-thin';
    this.list.setAttribute('role', 'listbox');
    this.list.tabIndex = -1;
    if (label) this.list.setAttribute('aria-labelledby', label.id);

    /* optgroup wird zur Ueberschrift, die Optionen darunter gehoeren zur Gruppe (fuer das Ausblenden beim Filtern) */
    this.groups = [];
    const entries = [];
    [...this.native.children].forEach((child) => {
      if (child.tagName === 'OPTGROUP') {
        const heading = document.createElement('li');
        heading.className = 'select__group';
        heading.setAttribute('role', 'presentation');
        heading.textContent = child.label;
        heading.groupItems = [];
        this.groups.push(heading);
        entries.push(heading);
        [...child.children].forEach((option) => entries.push({ option, heading }));
      } else if (child.tagName === 'OPTION') {
        entries.push({ option: child, heading: null });
      }
    });
    this.options = [];
    const rows = entries.map((entry) => {
      if (entry instanceof HTMLElement) return entry;
      const { option, heading } = entry;
      const index = option.index;
      const item = document.createElement('li');
      item.className = 'select__option';
      item.id = `${id}-option-${index}`;
      item.setAttribute('role', 'option');
      item.tabIndex = -1;
      item.dataset.value = option.value;
      item.dataset.index = String(index);
      if (heading) heading.groupItems.push(item);
      item.dataset.search = `${option.text} ${option.dataset.meta || ''}`.toLowerCase();
      const flag = option.dataset.flag ? `<img class="select__flag" src="${option.dataset.flag}" alt="" width="24" height="18" loading="lazy">` : '';
      const image = option.dataset.image
        ? `<img class="select__image" src="${option.dataset.image}" alt="" width="120" height="120" loading="lazy">`
        : this.mitBildern
          ? '<span class="select__image select__image--empty" aria-hidden="true"></span>'
          : '';
      const meta = option.dataset.meta ? `<span class="select__meta">${option.dataset.meta}</span>` : '';
      const notifyLabel = this.dataset.notifyLabel || '';
      const bell = option.dataset.notify ? `<span class="select__bell" title="${notifyLabel}">${BELL}<span class="visually-hidden">${notifyLabel}</span></span>` : '';
      if (option.disabled) item.setAttribute('aria-disabled', 'true');
      if (option.dataset.soldOut) item.classList.add('is-sold-out');
      if (option.dataset.notify) item.classList.add('has-bell');
      const sub = option.dataset.sub ? `<span class="select__option-sub">${option.dataset.sub}</span>` : '';
      item.innerHTML = `${flag}${image}<span class="select__option-text"><span class="select__option-label">${option.text}</span>${sub}</span>${meta}${bell}${CHECK}`;
      item.addEventListener('click', () => this.select(item));
      this.options.push(item);
      return item;
    });
    this.list.append(...rows);

    this.empty = document.createElement('p');
    this.empty.className = 'select__empty';
    this.empty.textContent = this.dataset.empty || '';
    this.empty.hidden = true;

    this.scroller.append(this.list);
    this.panel.append(this.scroller, this.empty);
    this.append(this.button, this.panel);

    this.button.addEventListener('click', () => (this.isOpen ? this.close(true) : this.open()));
    this.button.addEventListener('keydown', (event) => {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        this.open();
      }
    });
    this.panel.addEventListener('keydown', (event) => this.onKeydown(event));
    this.search?.addEventListener('input', () => this.filter(this.search.value));
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close(false);
    });
    this.sync();
  }

  /* Bottom Sheet auf kleinen Bildschirmen, nur mit data-sheet und wenn kg-dialog verfügbar ist. */
  get useSheet() {
    return this.hasAttribute('data-sheet') && window.matchMedia(SHEET_QUERY).matches && Boolean(customElements.get('kg-dialog'));
  }

  buildSheet() {
    this.sheet = document.createElement('kg-dialog');
    this.sheet.className = 'select__sheet';
    const dialog = document.createElement('dialog');
    /*
      Marker am Schuber selbst, nicht am Elternteil: Bei Bundles haengt `kg-bundle-steps` den
      Schuber zu sich um, er ist dann kein Nachfahre des `kg-select` mehr. Ein Selektor ueber
      den Vorfahren griff dort deshalb nie — die Sortenauswahl am Produkt sah anders aus als
      dieselbe Auswahl im Bundle.
    */
    const sortenAuswahl = this.classList.contains('select--options');
    dialog.className = `drawer drawer--sheet select-sheet${sortenAuswahl ? ' select-sheet--options' : ''}`;
    dialog.setAttribute('scroll-lock', '');
    const title = this.dataset.sheetTitle || this.label?.textContent?.trim() || '';
    dialog.setAttribute('aria-label', title);
    dialog.innerHTML = `<div class="drawer__handle" aria-hidden="true"></div><div class="drawer__header"><h2 class="drawer__title h5"></h2><button class="drawer__close" type="button" data-dialog-close>${CLOSE}<span class="visually-hidden"></span></button></div><div class="drawer__body select-sheet__body"></div>`;
    dialog.querySelector('.drawer__title').textContent = title;
    dialog.querySelector('.drawer__close .visually-hidden').textContent = this.dataset.closeLabel || '';
    this.sheet.append(dialog);
    this.sheetBody = dialog.querySelector('.select-sheet__body');
    this.append(this.sheet);
    this.sheet.addEventListener('dialog:close', () => this.afterSheetClose());
  }

  afterSheetClose() {
    this.panel.hidden = true;
    this.folgeNichtMehr();
    this.button.after(this.panel);
    this.button.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  get visibleOptions() {
    return this.options.filter((item) => !item.hidden);
  }

  sync() {
    const selected = this.native.selectedOptions[0];
    /*
      Mit data-title steht oben der feste Name der Zeile und darunter die gewaehlte Option —
      so wie bei den Bundle-Bestandteilen, wo der Bestandteil die Hauptzeile ist. Ohne
      data-title bleibt es beim gewohnten Aufbau.
    */
    const titel = this.dataset.title || '';
    this.value.textContent = titel || (selected ? selected.text : '');
    if (selected?.dataset.flag) {
      this.flag.src = selected.dataset.flag;
      this.flag.hidden = false;
    } else {
      this.flag.hidden = true;
    }
    if (selected?.dataset.image) {
      this.image.src = selected.dataset.image;
      this.image.hidden = false;
      this.image.classList.remove('select__image--empty');
    } else if (this.mitBildern) {
      // Gleiche Flaeche wie ein Bild, damit der Name nicht nach links springt
      this.image.removeAttribute('src');
      this.image.hidden = false;
      this.image.classList.add('select__image--empty');
    } else {
      this.image.hidden = true;
    }
    let meta = selected?.dataset.meta || this.dataset.note || '';
    if (titel) {
      /*
        „Orange Juice / 1.000g Beutel" — die Wahl und das, was sonst noch dazugehoert.
        Hinweise wie „Ausverkauft" bleiben draussen: Sie stehen in der Liste an der Sorte,
        im geschlossenen Zustand waeren sie eine Wiederholung.
      */
      meta = [selected ? selected.text : '', selected?.dataset.extra].filter(Boolean).join(' / ');
    }
    if (meta) {
      this.valueMeta.textContent = meta;
      this.valueMeta.hidden = false;
    } else {
      this.valueMeta.hidden = true;
    }
    this.options.forEach((item) => {
      item.setAttribute('aria-selected', String(item.dataset.index === String(this.native.selectedIndex)));
    });
  }

  /*
    Uebergibt die Liste an einen fremden Schuber (die Schrittfuehrung bei Bundles) und holt
    sie zurueck. Die Liste selbst bleibt dieselbe — sie haengt nur voruebergehend woanders.
  */
  sheetUebernehmen(ziel) {
    ziel.append(this.panel);
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    if (this.search) {
      this.search.value = '';
      this.filter('');
    }
    this.mittigWennOffen();
  }

  sheetAbgeben() {
    if (!this.classList.contains('is-open')) return;
    this.afterSheetClose();
  }

  open() {
    if (this.isOpen) return;
    this.loesePosition();
    /*
      Bei einem Bundle fuehrt eine gemeinsame Schrittfolge durch alle Bestandteile, statt je
      Dropdown einen eigenen Schuber zu oeffnen. Meldet sie sich zustaendig, ist hier Schluss.
    */
    if (this.useSheet && this.closest('kg-bundle-steps')?.oeffne?.(this)) return;
    if (this.useSheet) {
      if (!this.sheet) this.buildSheet();
      this.sheetBody.append(this.panel);
      this.panel.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      this.sheet.open(this.button);
      if (this.search) {
        this.search.value = '';
        this.filter('');
      }
      this.mittigWennOffen();
      return;
    }
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.macheUntenPlatz();
    this.positioniere();
    this.folgeMit();
    if (this.search) {
      this.search.value = '';
      this.filter('');
      this.search.focus({ preventScroll: true });
    } else {
      /* Wie im Schuber: Die gewaehlte Sorte steht mittig, nicht am unteren Rand der Liste. */
      this.focusSelected(true);
    }
  }

  close(focusButton) {
    if (!this.isOpen) return;
    /*
      In der Schrittfolge schliesst eine Wahl den Schuber nicht — dort geht es mit „Weiter"
      zum naechsten Bestandteil.
    */
    if (this.closest('kg-bundle-steps')?.sheet?.isOpen) return;
    if (this.sheet?.isOpen) {
      this.sheet.close();
      return;
    }
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    this.folgeNichtMehr();
    window.clearTimeout(this.ruckZeit);
    this.untenErzwungen = false;
    this.loesePosition();
    if (focusButton) this.button.focus();
  }

  select(item) {
    this.native.selectedIndex = Number(item.dataset.index);
    this.native.dispatchEvent(new Event('change', { bubbles: true }));
    this.close(true);
  }

  /*
    Die gewaehlte Sorte mittig stellen, aber erst wenn der Schuber wirklich offen ist.

    `open()` kehrt sofort zurueck und oeffnet zwei Bildaufbauten spaeter — der Vorlauf,
    den die Abdunklung der Browserleisten braucht (siehe dialog.js). Direkt danach
    gemessen ist die Liste noch null hoch, und es wird nirgendwohin gescrollt.
  */
  mittigWennOffen() {
    if (this.sheet?.isOpen) {
      this.focusSelected(true);
      return;
    }
    this.sheet?.addEventListener('dialog:open', () => this.focusSelected(true), { once: true });
  }

  focusSelected(mittig) {
    const selected = this.visibleOptions.find((item) => item.getAttribute('aria-selected') === 'true');
    this.fokussiere(selected || this.visibleOptions[0], mittig);
  }

  /*
    Fokus setzen, ohne dass der Browser dafuer scrollt. Von sich aus holt er die Option ins
    Bild und scrollt dafuer den naechsten Bereich, der es kann — im Schnellkauf ist das der
    Koerper des Schubers, und dabei wandert der Knopf samt Liste nach oben aus dem Bild.
    Sichtbar gemacht wird hier selbst, und zwar nur innerhalb der Liste.
  */
  fokussiere(item, mittig) {
    if (!item) return;
    item.focus({ preventScroll: true });
    if (!this.list) return;
    const ziel = item.getBoundingClientRect();
    const liste = this.list.getBoundingClientRect();
    /*
      Beim Oeffnen eines Schubers steht die gewaehlte Sorte mittig, nicht am Rand. Das knappe
      Heranscrollen darunter setzt sie genau auf die Unterkante — dort liegt der Verlauf von
      `kg-scroll-fade` darueber, sie wirkt angeschnitten, und dass darunter noch Sorten stehen,
      sieht man nicht. Der Browser begrenzt `scrollTop` selbst: Ist die Wahl die erste oder die
      letzte, bleibt sie oben beziehungsweise unten, und ohne Scrollbereich passiert nichts.
    */
    if (mittig) {
      this.list.scrollTop += ziel.top - liste.top - (liste.height - ziel.height) / 2;
      return;
    }
    if (ziel.top < liste.top) this.list.scrollTop += ziel.top - liste.top;
    else if (ziel.bottom > liste.bottom) this.list.scrollTop += ziel.bottom - liste.bottom;
  }

  filter(query) {
    if (this.clear) this.clear.hidden = query.length === 0;
    const needle = query.trim().toLowerCase();
    let visible = 0;
    this.options.forEach((item) => {
      const match = !needle || item.dataset.search.includes(needle);
      item.hidden = !match;
      if (match) visible += 1;
    });
    this.groups.forEach((heading) => {
      heading.hidden = !heading.groupItems.some((item) => !item.hidden);
    });
    this.empty.hidden = visible > 0;
    this.positioniere();
  }

  /*
    Die Liste haengt am Knopf statt im Textfluss unter ihm, denn als absolut gesetztes Kind
    schnitt sie jeder scrollende Vorfahr ab: im Schnellkauf der Koerper des Schubers, der seit
    dem 06.10. am Rechner nur noch so hoch ist wie sein Inhalt.

    Gerechnet wird in zwei Schritten, weil `fixed` nicht immer am Fenster haengt: Traegt ein
    Vorfahr `transform` oder `filter` — beim Schuber waehrend des Hereinfahrens —, ist er der
    Bezugsrahmen. Deshalb erst auf 0/0 setzen, messen wo das landet, und die Strecke bis zum
    Ziel als Abstand eintragen. Das stimmt in beiden Faellen.
  */
  /*
    Das Dropdown klappt nach unten auf. Reicht der Platz dort nicht, rueckt die Seite ein Stueck
    hoch, statt die Liste ueber den Knopf zu legen: Eine Liste, die mal oben und mal unten
    erscheint, laesst den Blick bei jedem Oeffnen neu suchen.

    Nur so weit, dass der Knopf unter der Kopfleiste sichtbar bleibt, und nur beim Oeffnen —
    beim Nachfuehren waehrend des Scrollens wuerde sich die Seite sonst immer weiter schieben.
  */
  macheUntenPlatz() {
    const knopf = this.button.getBoundingClientRect();
    /*
      Nach dem tatsaechlichen Bedarf, nicht nach der Deckelung: Ein kurzes Sortier-Dropdown mit
      vier Eintraegen soll die Seite nicht um 300 px schieben, nur weil eine Sortenliste so viel
      braucht. Gemessen wird ohne Begrenzung, wie `positioniere` es gleich darauf auch tut.
    */
    const gemerkt = this.panel.style.maxHeight;
    this.panel.style.maxHeight = 'none';
    const will = Math.min(MAX_HOEHE, this.panel.offsetHeight);
    this.panel.style.maxHeight = gemerkt;
    const unten = window.innerHeight - knopf.bottom - 12;
    if (unten >= will - 2) return;

    /*
      Drei Grenzen: Mehr als die Liste braucht, waere unnoetig; weiter als bis unter die
      Kopfleiste schoebe den Knopf aus dem Bild; und weiter als die Seite hergibt geht ohnehin
      nicht — am Seitenende bleibt es deshalb beim gewohnten Umklappen.
    */
    const kopf = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
    const seite = document.scrollingElement || document.documentElement;
    const rest = Math.max(0, seite.scrollHeight - window.innerHeight - window.scrollY);
    const weg = Math.min(will - unten, Math.round(knopf.top - kopf - 12), rest);
    if (weg <= 2 || unten + weg < Math.min(will, MIN_UNTEN)) return;

    /*
      Die Bewegung laeuft weich, `html` traegt dafuer `scroll-behavior: smooth`. Waehrend sie
      laeuft, ist unten noch zu wenig Platz — ohne Festlegung klappte `positioniere` die Liste
      erst nach oben und mitten in der Bewegung wieder nach unten, ein sichtbares Umspringen.
      Deshalb stehen Richtung und Endhoehe fuer die Dauer fest: Die Liste haengt am Knopf und
      faehrt mit der Seite ins Bild, statt zu springen oder zu wachsen.
    */
    this.untenErzwungen = true;
    this.zielHoehe = unten + weg;
    window.clearTimeout(this.ruckZeit);
    this.ruckZeit = window.setTimeout(() => {
      this.untenErzwungen = false;
    }, 700);
    window.scrollBy({ top: weg });
  }

  positioniere() {
    if (!this.isOpen || this.sheet?.isOpen || this.panel.parentElement !== this) return;
    const panel = this.panel;
    const knopf = this.button.getBoundingClientRect();
    /* Luft zwischen Knopf und Liste, in beide Richtungen */
    const abstand = 10;
    const rand = 8;

    /*
      Erst ohne Begrenzung messen, wie breit und wie hoch die Liste sein will. Die Breite
      kommt vom Inhalt, mindestens aber vom Knopf: Bei der Sortierung ist der Knopf nur so
      breit wie das gewaehlte Wort, und eine Liste in dieser Breite quetscht ihre Eintraege.
      Nach oben gedeckelt, damit ein langer Eintrag die Liste nicht ueber das Fenster zieht.
    */
    /*
      Das Messen gibt die Hoehe kurz frei; ohne Begrenzung gibt es nichts zu scrollen und der
      Stand faellt auf null. Deshalb merken und danach zuruecksetzen.
    */
    const stand = this.list ? this.list.scrollTop : 0;
    panel.style.left = '0px';
    panel.style.top = '0px';
    panel.style.maxHeight = 'none';
    /*
      Aufrunden, nicht runden: Der Knopf ist 477,08 px breit und beginnt auf einem halben Pixel.
      Gerundet war das Popover 0,48 px schmaler als er — und genau dort blitzten die Kacheln der
      Groessen dahinter als Haarlinie durch. Breite aufgerundet und die linke Kante abgerundet
      heisst: Das Popover deckt den Knopf immer vollstaendig ab, hoechstens einen halben Pixel
      zu viel, und der liegt ueber dem Seitengrund.
    */
    panel.style.minWidth = `${Math.ceil(knopf.width)}px`;
    panel.style.width = 'max-content';
    panel.style.maxWidth = `${Math.round(Math.min(window.innerWidth - 2 * rand, 384))}px`;
    const nullpunkt = panel.getBoundingClientRect();
    const willHoehe = panel.offsetHeight;
    const breite = panel.offsetWidth;

    const unten = window.innerHeight - knopf.bottom - abstand - rand;
    const oben = knopf.top - abstand - rand;
    /*
      Nach oben nur als letzter Ausweg: wenn unten nicht einmal drei Zeilen hinpassen und die
      Seite nicht weiter hochruecken konnte, etwa am Seitenende. Die Schwelle „passt unten
      nicht vollstaendig" waere zu scharf — auf einem 900 px hohen Fenster traefe sie am
      Seitenanfang die Regel statt die Ausnahme.
    */
    const nachOben = !this.untenErzwungen && unten < Math.min(willHoehe, MIN_UNTEN) && oben > unten;
    const raum = this.untenErzwungen ? Math.max(unten, this.zielHoehe || 0) : nachOben ? oben : unten;
    panel.style.maxHeight = `${Math.max(120, Math.min(MAX_HOEHE, raum))}px`;

    const zielOben = nachOben ? Math.max(rand, knopf.top - abstand - panel.offsetHeight) : knopf.bottom + abstand;
    /* Breiter als der Knopf heisst: Sie koennte rechts aus dem Bild laufen, dann nach links ruecken. */
    const zielLinks = Math.max(rand, Math.min(knopf.left, window.innerWidth - rand - breite));
    panel.style.left = `${Math.floor(zielLinks - nullpunkt.left)}px`;
    panel.style.top = `${Math.round(zielOben - nullpunkt.top)}px`;
    if (this.list) this.list.scrollTop = stand;

    /*
      Haelt die Liste mehr Eintraege, als zu sehen sind? Dann braucht es rechts Platz fuer die
      Leiste. Das steht hier, weil erst nach dem Setzen der Hoehe feststeht, ob sie scrollt.
    */
    panel.classList.toggle('has-scroll', Boolean(this.list) && this.list.scrollHeight > this.list.clientHeight + 1);
    /*
      Die gemessene Breite der Leiste, damit die Verlaeufe davor aufhoeren koennen. Gemessen
      statt fest eingetragen: Chromium nimmt die gesetzten 6 px, Firefox entscheidet selbst, und wo die
      Leiste ueber dem Inhalt schwebt (macOS), kommt null heraus — dann laeuft der Verlauf wie
      bisher durch, was dort auch richtig ist.
    */
    const leiste = this.list ? this.list.offsetWidth - this.list.clientWidth : 0;
    panel.style.setProperty('--scrollbar', `${Math.max(0, Math.round(leiste))}px`);
  }

  loesePosition() {
    ['width', 'min-width', 'max-width', 'left', 'top', 'max-height'].forEach((name) =>
      this.panel?.style.removeProperty(name),
    );
  }

  /* Solange die Liste offen ist, folgt sie dem Knopf — auch wenn die Seite oder der Schuber darunter scrollt. */
  folgeMit() {
    if (this.folger) return;
    this.folger = (ereignis) => {
      /*
        Scrollt der Besucher in der Liste selbst, bleibt alles stehen. Neu zu messen hiesse,
        die Hoehe kurz freizugeben — und dabei faellt der Scrollstand auf null. Der Knopf
        bewegt sich dabei ohnehin nicht.
      */
      const ziel = ereignis?.target;
      if (ziel instanceof Node && this.panel.contains(ziel)) return;
      this.positioniere();
    };
    window.addEventListener('scroll', this.folger, true);
    window.addEventListener('resize', this.folger);
    window.visualViewport?.addEventListener('resize', this.folger);
    window.visualViewport?.addEventListener('scroll', this.folger);
  }

  folgeNichtMehr() {
    if (!this.folger) return;
    window.removeEventListener('scroll', this.folger, true);
    window.removeEventListener('resize', this.folger);
    window.visualViewport?.removeEventListener('resize', this.folger);
    window.visualViewport?.removeEventListener('scroll', this.folger);
    this.folger = null;
  }

  move(delta) {
    const items = this.visibleOptions;
    if (!items.length) return;
    const current = items.indexOf(document.activeElement);
    let next = current + delta;
    if (current === -1) next = delta > 0 ? 0 : items.length - 1;
    next = Math.max(0, Math.min(items.length - 1, next));
    this.fokussiere(items[next]);
  }

  onKeydown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close(true);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.search && document.activeElement === this.visibleOptions[0]) this.search.focus();
        else this.move(-1);
        break;
      case 'Home':
        event.preventDefault();
        this.fokussiere(this.visibleOptions[0]);
        break;
      case 'End':
        event.preventDefault();
        this.fokussiere(this.visibleOptions.at(-1));
        break;
      case 'Enter':
      case ' ':
        if (document.activeElement?.classList.contains('select__option')) {
          event.preventDefault();
          this.select(document.activeElement);
        } else if (event.key === 'Enter' && this.search) {
          event.preventDefault();
          this.visibleOptions[0] && this.select(this.visibleOptions[0]);
        }
        break;
      case 'Tab':
        this.close(false);
        break;
      default:
        break;
    }
  }
}

customElements.define('kg-select', KgSelect);
